import { Link } from "react-router-dom";
import { ArrowRight, Award, Gift, CalendarHeart, Handshake } from "lucide-react";
import { PageHero, Overline } from "@/components/Bits";
import { FocusAreas } from "@/components/FocusAreas";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";

const DELIVERY = [
  { icon: Award, title: "Scholarships", text: "Direct funding that removes financial barriers to education and training." },
  { icon: Gift, title: "Grants", text: "Targeted support for programs, equipment, and community initiatives." },
  { icon: CalendarHeart, title: "Programs", text: "Mentorship, clinics, and workshops delivered where they're needed most." },
  { icon: Handshake, title: "Partnerships", text: "Collaborations with schools, teams, and organizations to scale impact." },
];

export default function Programs() {
  return (
    <>
      <PageHero
        label="Programs"
        title="How we turn support into"
        accent="opportunity."
        subtitle="We work across five focus areas, delivered through scholarships, grants, programs, and community partnerships designed for measurable, lasting impact."
      />

      <FocusAreas />

      <section className="border-y border-border bg-secondary/50">
        <div className="mx-auto max-w-7xl px-5 py-24 md:px-8 md:py-28">
          <Reveal><Overline>How We Deliver</Overline></Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-6 max-w-2xl font-heading text-3xl font-bold tracking-tight sm:text-4xl">
              Four pathways to <span className="italic text-gold">real change.</span>
            </h2>
          </Reveal>
          <Stagger className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {DELIVERY.map((d) => (
              <StaggerItem key={d.title}>
                <div className="flair-card h-full rounded-2xl border border-border bg-card p-7">
                  <span className="flair-icon grid h-12 w-12 place-items-center rounded-full bg-gold/12 text-gold ring-1 ring-gold/25">
                    <d.icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-6 font-heading text-lg font-semibold">{d.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">{d.text}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-24 text-center md:px-8 md:py-28">
        <Reveal>
          <h2 className="mx-auto max-w-3xl font-heading text-3xl font-bold tracking-tight sm:text-4xl">
            Fund a program. Change a trajectory.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/donate" className="inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-semibold text-navy-deep transition-transform hover:-translate-y-0.5" data-testid="programs-donate">
              Support a Program <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/get-involved?tab=partner" className="inline-flex items-center gap-2 rounded-full border border-border px-7 py-3.5 text-sm font-semibold transition-colors hover:border-gold hover:text-gold" data-testid="programs-partner">
              Partner With Us
            </Link>
          </div>
        </Reveal>
      </section>
    </>
  );
}
